<template>
    <div
        class="tw:relative"
        ref="tooltipContainerRef"
    >
        <div class="venue-map" :class="[{ 'venue-map--loading': loading, 'venue-map--ready': isReady }, wrapperClass]">
            <!-- Loading skeleton -->
            <Skeleton
                v-if="loading || !svgMarkup"
                role="status"
                :aria-label="$t('seat_picker.loading')"
                padding="tw:p-6 tw::w-full tw:h-full"
                custom-class="tw:shadow-none"
            >
                <template #skeleton>
                    <SkeletonItem height="tw:h-44 tw:xxs:h-54 tw:md:h-full tw:w-full tw:md:w-2/3 tw:lg:w-1/2 tw:md:mx-auto tw:rounded-xl" />
                </template>
            </Skeleton>

            <!-- SVG container -->
            <div
                v-show="!loading && svgMarkup"
                ref="mapContainerRef"
                class="venue-map__svg-container"
                :class="containerClass"
                v-html="svgMarkup"
            ></div>

            <!-- Empty state -->
            <p
                v-if="!loading && !svgMarkup"
                class="tw:text-sm tw:text-gray-400 tw:text-center tw:py-8 tw:m-0"
            >
                {{ $t('seat_picker.empty') }}
            </p>
        </div>

        <!-- Tooltip flotante sobre zonas del mapa -->
        <ZoneMapTooltip
            :zone="tooltipZone"
            :x="tooltipX"
            :y="tooltipY"
        />
    </div>
</template>

<script setup>
    import { ref, watch, onMounted } from 'vue';
    import { useVenueMap } from '@/composables/front/ticketing/useVenueMap';
    import Skeleton from '@/modules/front/ui/skeleton/Skeleton.vue';
    import SkeletonItem from '@/modules/front/ui/skeleton/SkeletonItem.vue';
    import ZoneMapTooltip from '@/modules/front/ticketing/ZoneMapTooltip.vue';

    // ─── Props ────────────────────────────────────────────────────────────────
    const props = defineProps({
        /** Raw SVG string to render inline via v-html. */
        svgMarkup: {
            type: String,
            default: '',
        },
        /** Array of zone objects. Each must have at least { id, esta_disponible? }. */
        zones: {
            type: Array,
            default: () => [],
        },
        /** Currently selected zone id (controlled from parent). */
        selectedZoneId: {
            type: [String, Number],
            default: null,
        },
        /** CSS class that identifies zone nodes inside the SVG. */
        zoneClass: {
            type: String,
            default: 'recinto-zona',
        },
        /** data-* attribute name holding the zone id (e.g. data-region → 'region'). */
        dataAttr: {
            type: String,
            default: 'region',
        },
        /** Extra CSS classes applied to the root wrapper. */
        wrapperClass: {
            type: [String, Array, Object],
            default: '',
        },
        /** Extra CSS classes applied to the inner SVG container div. */
        containerClass: {
            type: [String, Array, Object],
            default: '',
        },
        /** Whether the parent is still loading the SVG or zone data. */
        loading: {
            type: Boolean,
            default: false,
        },
        /** Zone id currently hovered from an external source (e.g. ZoneTable row). */
        hoveredZoneId: {
            type: [String, Number],
            default: null,
        },
    });

    // ─── Emits ────────────────────────────────────────────────────────────────
    const emit = defineEmits([
        'zone-click',
        'zone-hover',
        'zone-colors',
    ]);

    // ─── DOM refs ─────────────────────────────────────────────────────────────
    const mapContainerRef = ref(null);
    const tooltipContainerRef = ref(null);

    // ─── Tooltip state ────────────────────────────────────────────────────────
    const tooltipZone = ref(null);
    const tooltipX = ref(0);
    const tooltipY = ref(0);

    // ─── Reactive refs mirroring props (composable needs refs, not raw values) ──
    const zonesRef = ref(props.zones);
    const selectedZoneIdRef = ref(props.selectedZoneId);

    // ─── Composable ───────────────────────────────────────────────────────────
    const {
        isReady,
        attachMapListeners,
        syncSelectedZone,
        syncHoveredZone,
        zoneColors,
    } = useVenueMap({
        zones: zonesRef,
        selectedZoneId: selectedZoneIdRef,
        zoneClass: props.zoneClass,
        dataAttr: props.dataAttr,
        onZoneClick: zone => emit('zone-click', zone),
        onZoneHover: (zoneId, entering, event) => {
            // Actualizar tooltip
            if (entering && event) {
                const rect = tooltipContainerRef.value?.getBoundingClientRect();
                tooltipX.value = event.clientX - (rect?.left ?? 0);
                tooltipY.value = event.clientY - (rect?.top ?? 0);
                tooltipZone.value = zonesRef.value.find(z => z.id === zoneId) ?? null;
            } else {
                tooltipZone.value = null;
            }
            emit('zone-hover', zoneId, entering, event);
        },
    });

    // ─── Keep internal refs in sync with props ────────────────────────────────
    watch(() => props.zones, val => { zonesRef.value = val; }, { immediate: true });
    watch(() => props.selectedZoneId, val => {
        selectedZoneIdRef.value = val;
        syncSelectedZone(mapContainerRef.value);
    });

    // ─── (Re-)attach listeners when the SVG content changes ──────────────────
    //    setTimeout con delay generoso, igual que el legacy SvgComponent (1000ms).
    //    El SVG inyectado via v-html necesita tiempo para que el navegador lo
    //    parse, construya el DOM y aplique layout antes de que querySelectorAll
    //    y la sanitización de fills rgba() puedan funcionar.
    watch(() => props.svgMarkup, val => {
        if (!val) return;
        setTimeout(() => {
            attachMapListeners(mapContainerRef.value);
        }, 1000);
    });

    // ─── Emit zone colours whenever the map is re-attached ───────────────────
    watch(zoneColors, val => {
        if (Object.keys(val).length) emit('zone-colors', val);
    });

    // ─── Sync external hover highlight ───────────────────────────────────────
    watch(() => props.hoveredZoneId, (newId, oldId) => {
        syncHoveredZone(mapContainerRef.value, newId, oldId);
    });

    // ─── Initial mount (when svgMarkup is already present on mount) ──────────
    onMounted(() => {
        if (props.svgMarkup) {
            setTimeout(() => {
                attachMapListeners(mapContainerRef.value);
            }, 1000);
        }
    });
</script>

<style>
/*
 * SVG responsive sizing
 *
 * Strategy: fixed height via wrapperClass
 * ─────────────────────────────────────────────────────────────────────────────
 * The parent passes a height class via `wrapperClass` (e.g. "tw:h-80").
 * The SVG fills 100% of that container via width/height 100%.
 *
 * The inline <svg> (v-html) often carries hardcoded width/height attributes.
 * We force it to fill its container via width/height 100% and rely on the
 * SVG's viewBox + preserveAspectRatio for correct proportions.
 *
 * If the SVG has no viewBox the browser still scales it, but proportions
 * may be off — that is an SVG authoring issue outside this component's scope.
 */
.venue-map__svg-container {
    /* Fill parent width; height comes from aspect-ratio (set inline) */
    width: 100%;
    display: block;
    overflow: hidden;
}

.venue-map__svg-container > svg {
    /* Override any hardcoded width/height attributes on the injected SVG */
    width: 100% !important;
    height: 100% !important;
    display: block;
}

/* Available zone — pointer cursor; full opacity always, hover via fill shift */
.venue-map .recinto-zona:not(.no-disponible) {
    cursor: pointer;
    opacity: 1 !important;
    transition: filter 0.15s, fill 0.15s;
}
/* Forzar opacidad total en TODOS los hijos de zonas disponibles.
   El SVG del backend a veces incrusta transparencia en nodos anidados
   (<g>, <path>, <polygon>) vía opacity / fill-opacity / rgba().
   Con !important en raíz + hijos, el CSS garantiza opacidad plena
   sin depender de que _clearOpacity del composable llegue a tiempo. */
.venue-map .recinto-zona:not(.no-disponible),
.venue-map .recinto-zona:not(.no-disponible) * {
    opacity: 1 !important;
    fill-opacity: 1 !important;
}
.venue-map .recinto-zona:not(.no-disponible):hover {
    filter: brightness(0.88);
}

/* Unavailable zone */
.venue-map .recinto-zona.no-disponible {
    cursor: not-allowed;
    opacity: 0.45;
}

/* Selected zone — fill+text handled inline by the composable; no border/stroke */
.venue-map .venue-map__zone--selected {
    filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.45));
    opacity: 1 !important;
    outline: none;
}

/* External hover highlight (e.g. from ZoneTable row) */
.venue-map .recinto-zona.highlighted-svg {
    fill: var(--altcolor) !important;
    stroke: var(--alttextcolor, #fff);
    stroke-width: 3px;
    transition: fill 0.2s, opacity 0.2s, stroke 0.2s;
}
</style>
